import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';

export type OrbitNote = {
  noteid: number;
  note_text: string;
  tags: string[];
  created_at: string;
  created_by: string;
};

type ResolvedStakeholder =
  | { kind: 'client'; data: any }
  | { kind: 'agent'; data: any };

export async function resolveStakeholder(
  stakeholderId: string
): Promise<ResolvedStakeholder | null> {
  let clientData: any = null;
  try {
    clientData = await ClientService.getClientById(stakeholderId);
  } catch {
    clientData = null;
  }
  if (clientData) return { kind: 'client', data: clientData };

  let agentData: any = null;
  try {
    agentData = await AgentsService.getAgent(stakeholderId);
  } catch {
    agentData = null;
  }
  if (agentData) return { kind: 'agent', data: agentData };
  return null;
}

export function parseNotesFromField(notesField: any): {
  notes: any[];
  recentActivity: any[];
} {
  let notes: any[] = [];
  let recentActivity: any[] = [];
  if (!notesField) return { notes, recentActivity };

  try {
    const parsed = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
    if (Array.isArray(parsed)) {
      notes = parsed;
    } else if (typeof parsed === 'object' && parsed !== null) {
      if (Array.isArray(parsed.notes)) {
        notes = parsed.notes;
        recentActivity = Array.isArray(parsed.recent_activity) ? parsed.recent_activity : [];
      } else {
        notes = [parsed];
      }
    }
  } catch (error) {
    console.error('Error parsing notes:', error);
  }
  return { notes, recentActivity };
}

/** Ensure every note has a stable numeric noteid (backfills legacy notes missing one). */
export function ensureNoteIds(notes: any[]): any[] {
  return notes.map((note, idx) => ({
    ...note,
    noteid: note?.noteid != null && note.noteid !== '' ? Number(note.noteid) : idx + 1,
  }));
}

export function nextNoteId(notes: any[]): number {
  const ids = notes
    .map((n) => Number(n?.noteid))
    .filter((id) => Number.isFinite(id) && id >= 0);
  return (ids.length ? Math.max(...ids) : 0) + 1;
}

function getClientFieldValue(clientData: any, field: string, isDateField = false) {
  const snakeValue = clientData[field];
  const pascalKey = field
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const capitalizedKey = field.charAt(0).toUpperCase() + field.slice(1);
  const pascalValue = clientData[pascalKey];
  const capitalizedValue = clientData[capitalizedKey];
  const value =
    snakeValue !== undefined && snakeValue !== null
      ? snakeValue
      : pascalValue !== undefined && pascalValue !== null
        ? pascalValue
        : capitalizedValue;

  if (isDateField) {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
  }
  return value !== null && value !== undefined ? value : '';
}

function buildClientUpdatePayload(clientData: any, notes: any[]) {
  return {
    FirstName: getClientFieldValue(clientData, 'first_name'),
    LastName: getClientFieldValue(clientData, 'last_name'),
    Company: getClientFieldValue(clientData, 'company'),
    Email: getClientFieldValue(clientData, 'email'),
    Phone: getClientFieldValue(clientData, 'phone'),
    Address: getClientFieldValue(clientData, 'address'),
    Type: getClientFieldValue(clientData, 'type'),
    ContactName: getClientFieldValue(clientData, 'contact_name'),
    Prefix: getClientFieldValue(clientData, 'prefix'),
    MiddleName: getClientFieldValue(clientData, 'middle_name'),
    DateOfBirth: getClientFieldValue(clientData, 'date_of_birth', true),
    SecondaryPhoneNumber: getClientFieldValue(clientData, 'secondary_phone_number'),
    Entity: getClientFieldValue(clientData, 'entity'),
    Title: getClientFieldValue(clientData, 'title'),
    ContactCreatedDate: getClientFieldValue(clientData, 'contact_created_date', true),
    PrimaryAddressCity: getClientFieldValue(clientData, 'primary_address_city'),
    PrimaryAddressProvince: getClientFieldValue(clientData, 'primary_address_province'),
    PrimaryAddressPostalZip: getClientFieldValue(clientData, 'primary_address_postal_zip'),
    PrimaryAddressCountry: getClientFieldValue(clientData, 'primary_address_country'),
    SecondaryEmailAddress: getClientFieldValue(clientData, 'secondary_email_address'),
    SecondaryAddressStreet: getClientFieldValue(clientData, 'secondary_address_street'),
    SecondaryAddressCity: getClientFieldValue(clientData, 'secondary_address_city'),
    SecondaryAddressProvinceState: getClientFieldValue(
      clientData,
      'secondary_address_province_state'
    ),
    SecondaryAddressPostalZip: getClientFieldValue(clientData, 'secondary_address_postal_zip'),
    SecondaryAddressCountry: getClientFieldValue(clientData, 'secondary_address_country'),
    FormalNameIfDifferent: getClientFieldValue(clientData, 'formal_name_if_different'),
    Notes: JSON.stringify(notes),
    RecentActivity: getClientFieldValue(clientData, 'recent_activity'),
    AssignedLead: getClientFieldValue(clientData, 'assigned_lead'),
  };
}

function getAgentField(agentData: any, key: string) {
  const v = agentData[key];
  return v !== null && v !== undefined ? v : '';
}

function buildAgentUpdatePayload(
  agentData: any,
  notes: any[],
  recentActivity: any[]
) {
  return {
    FirstName: getAgentField(agentData, 'FirstName'),
    LastName: getAgentField(agentData, 'LastName'),
    Company: getAgentField(agentData, 'Company'),
    Email: getAgentField(agentData, 'Email'),
    Phone: getAgentField(agentData, 'Phone'),
    Address: getAgentField(agentData, 'Address'),
    Jurisdiction: getAgentField(agentData, 'Jurisdiction'),
    SecondaryPhoneNumber: getAgentField(agentData, 'SecondaryPhoneNumber'),
    Entity: getAgentField(agentData, 'Entity'),
    Title: getAgentField(agentData, 'Title'),
    ContactCreatedAt: getAgentField(agentData, 'ContactCreatedAt'),
    PrimaryAddressCity: getAgentField(agentData, 'PrimaryAddressCity'),
    PrimaryAddressProvince: getAgentField(agentData, 'PrimaryAddressProvince'),
    PrimaryAddressPostal: getAgentField(agentData, 'PrimaryAddressPostal'),
    PrimaryAddressCountry: getAgentField(agentData, 'PrimaryAddressCountry'),
    SecondaryEmail: getAgentField(agentData, 'SecondaryEmail'),
    SecondaryAddressStreet: getAgentField(agentData, 'SecondaryAddressStreet'),
    SecondaryAddressCity: getAgentField(agentData, 'SecondaryAddressCity'),
    SecondaryAddressProvince: getAgentField(agentData, 'SecondaryAddressProvince'),
    SecondaryAddressPostal: getAgentField(agentData, 'SecondaryAddressPostal'),
    SecondaryAddressCountry: getAgentField(agentData, 'SecondaryAddressCountry'),
    ContactType: getAgentField(agentData, 'ContactType'),
    FormalName: getAgentField(agentData, 'FormalName'),
    Notes: JSON.stringify({ notes, recent_activity: recentActivity }),
    ContactName: getAgentField(agentData, 'ContactName'),
    MiddleName: getAgentField(agentData, 'MiddleName'),
    DateOfBirth: getAgentField(agentData, 'DateOfBirth'),
  };
}

async function persistNotes(
  stakeholderId: string,
  notes: any[],
  recentActivity: any[],
  kind: 'client' | 'agent',
  data: any
): Promise<void> {
  if (kind === 'client') {
    await ClientService.updateClient(
      stakeholderId,
      buildClientUpdatePayload(data, notes)
    );
  } else {
    await AgentsService.updateAgent(
      stakeholderId,
      buildAgentUpdatePayload(data, notes, recentActivity)
    );
  }
}

/**
 * Load stakeholder, apply a transform to the notes array, and save.
 * For agents, preserves `recent_activity` alongside notes.
 */
export async function mutateStakeholderNotes(
  stakeholderId: string,
  transform: (notes: any[]) => any[]
): Promise<void> {
  const resolved = await resolveStakeholder(stakeholderId);
  if (!resolved) {
    throw new Error('Stakeholder not found.');
  }

  const notesField = resolved.data.Notes ?? resolved.data.notes;
  const { notes, recentActivity } = parseNotesFromField(notesField);
  // Backfill missing noteids so legacy notes match the UI's normalized ids
  // and can be edited/deleted reliably.
  const notesWithIds = ensureNoteIds(notes);
  const nextNotes = transform(notesWithIds);

  await persistNotes(
    stakeholderId,
    nextNotes,
    recentActivity,
    resolved.kind,
    resolved.data
  );
}

export async function updateStakeholderNote(
  stakeholderId: string,
  noteId: number,
  updates: { note_text: string; tags: string[] }
): Promise<void> {
  await mutateStakeholderNotes(stakeholderId, (notes) => {
    const index = notes.findIndex((n) => Number(n.noteid) === Number(noteId));
    if (index === -1) {
      throw new Error('Note not found.');
    }
    const updated = [...notes];
    updated[index] = {
      ...updated[index],
      note_text: updates.note_text,
      tags: updates.tags,
    };
    return updated;
  });
}

export async function deleteStakeholderNote(
  stakeholderId: string,
  noteId: number
): Promise<void> {
  await mutateStakeholderNotes(stakeholderId, (notes) => {
    const next = notes.filter((n) => Number(n.noteid) !== Number(noteId));
    if (next.length === notes.length) {
      throw new Error('Note not found.');
    }
    return next;
  });
}
